// Which puzzles have been solved: as the server knows it, and as this
// session has added to it.

import { api, isSignedOut, postJson, readJson } from "./api.ts";

export const reportKey = (game: string, color: string): string => `${game}:${color}`;

/** Reports fetched or produced this session, so screens can hand them on. */
export const reportCache = new Map<string, any>();

/** Solved plies per game, as the server knows them. */
export const solvedCache = new Map<string, Set<number>>();

/**
 * Solves this session has sent, per game. Kept apart from the set above,
 * which the screen showing the puzzle shares and redraws from: if "have I
 * already sent this" were answered by looking there, whichever of the two
 * updated it first would silently cancel the write.
 */
const sentSolves = new Map<string, Set<number>>();
function sentFor(key: string): Set<number> {
  let set = sentSolves.get(key);
  if (!set) sentSolves.set(key, (set = new Set()));
  return set;
}

export async function loadSolved(game: string, color: string): Promise<Set<number>> {
  const key = reportKey(game, color);
  const cached = solvedCache.get(key);
  if (cached) return cached;
  const set = new Set<number>();
  solvedCache.set(key, set);
  try {
    const data = await readJson<{ plies?: number[] }>(await api(`/api/solved?game=${encodeURIComponent(game)}&color=${color}`));
    for (const ply of data.plies || []) set.add(Number(ply));
  } catch (err) {
    if (isSignedOut(err)) throw err;
  }
  // Anything solved here since that the server had not stored yet.
  for (const ply of sentFor(key)) set.add(ply);
  return set;
}

/** Remember a solved puzzle, on the server and on screen. Says if it is news. */
export function markSolved(game: string, color: string, ply: number): boolean {
  const key = reportKey(game, color);
  const sent = sentFor(key);
  const known = solvedCache.get(key);
  const news = !sent.has(ply) && !(known && known.has(ply));
  sent.add(ply);
  known?.add(ply);
  if (news) postJson("/api/solved", { game, color, ply }).catch(() => {});
  return news;
}
